import { ApplyForm } from 'api/U002';
import { Button } from 'atoms/Button';
import { TransitionButton } from 'atoms/TransitionButton';
import { AuditionFormValues, transformAuditionFormValuesToA05Input } from 'commons/auditionForm';
import { deleteAnswers } from 'commons/auditionForm/autosave/utils';
import { useCreateApplyForm } from 'hooks/u002/useCreateApplyFormHooks';
import { useAuth } from 'hooks/useAuth';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { Answer } from 'organisms/u002/audition-form-confirm/Answer';
import { FC, useContext, useEffect, useState } from 'react';
import { AppContext } from 'templates/AppContextProvider';
import { AppFooter } from 'templates/AppFooter';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';

type AuditionFormProps = {
  title: string;
  audition_id: string;
  examination_no: number;
  /* valuesに対応するquestionを取得するめに必要 */
  applyForm: ApplyForm[];
  values: AuditionFormValues;
  onBack: () => void;
  onSuccess?: () => void;
};

export const AuditionFormConfirm: FC<AuditionFormProps> = (props) => {
  const auth = useAuth();
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const { t: tContentsHandler } = useTranslationWithLang('contentsHandler');
  const { currentLang, isGlobal } = useContext(AppContext);
  const isJapanese = currentLang === 'ja';

  const [createApplyForm, isLoading, err] = useCreateApplyForm();
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [errorModal, setErrorModal] = useState<string>();
  useEffect(() => {
    if (err) {
      setErrorModal(err.message);
    }
  }, [err]);
  // 回答情報をフォーム情報のsort_noで並び替え
  const valuesMap = new Map(props.values.map((value) => [value.apply_form_no, value]));
  const sortedValues = props.applyForm
    .toSorted(({ sort_no: a, apply_form_no: a2 }, { sort_no: b, apply_form_no: b2 }) =>
      a > 0 && b > 0 ? a - b : a2 - b2
    )
    .map((val) => valuesMap.get(`${val.apply_form_no}`))
    .filter((val) => val !== undefined);

  const handleBack = () => props.onBack();
  const handleNext = async () => {
    setIsFileUploading(true);
    const user_id_a = auth.username;
    try {
      const { answer_list, consent_list } = await transformAuditionFormValuesToA05Input(
        props.values,
        {
          audition_id: props.audition_id,
          examination_no: props.examination_no,
          user_id_a,
          consent_version: props.examination_no,
        }
      );
      console.debug(answer_list, consent_list);
      setIsFileUploading(false);
      createApplyForm({
        request: {
          user_id_a,
          answer_list,
          consent_list,
        },
        onSuccess: () => {
          deleteAnswers(props.audition_id, props.examination_no);
          props.onSuccess?.();
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        setErrorModal(e.message);
        return;
      }
      throw e;
    } finally {
      setIsFileUploading(false);
    }
  };

  // 初期化時にスクロール位置を上部に移動
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {(isLoading || isFileUploading) && <LoadingModal />}
      {errorModal && (
        <MessageModal
          message={errorModal}
          closeLabel={tAuditionForm('TU002_038')}
          onClose={() => setErrorModal(undefined)}
        />
      )}
      <div style={{ background: 'var(--c-background)' }}>
        <AppLogoHeader />
        <PageContent>
          <h1 className=" text-xl mb-16 text-center text-[--c-text]">{props.title}</h1>
          {sortedValues.map((value) => {
            const label = (() => {
              const applyForm = props.applyForm.find(
                (a) => `${a.apply_form_no}` === value.apply_form_no
              );
              if (applyForm == null) {
                return '';
              }
              // 海外ドメインの場合questionではなく独自の質問文を表示しているため、回答確認の質問文も併せて表示している
              if (applyForm.format === '12' && isGlobal) {
                return tContentsHandler('CH_014');
              }
              if (applyForm.format === '13' && isGlobal) {
                return tContentsHandler('CH_013');
              }
              return applyForm.question;
            })();
            const screenValue = (() => {
              const applyForm = props.applyForm.find(
                (a) => `${a.apply_form_no}` === value.apply_form_no
              );
              if (applyForm?.format === '52') {
                const val = value.answer as string;
                return { ...value, answer: val.split('\n')[0] };
              }
              return value;
            })();
            const etc = (() => {
              const applyForm = props.applyForm.find(
                (a) => `${a.apply_form_no}` === value.apply_form_no
              );
              if (applyForm == null || ['08', '09', '17'].includes(applyForm.format)) {
                return '';
              }
              if (applyForm.format === '54' && isGlobal) {
                return 'With member consent';
              }
              return value.answer_etc;
            })();
            return (
              <div key={value.apply_form_no}>
                <p className="text-lg font-bold mb-4">{label}</p>
                <Answer value={screenValue} />
                {etc && etc !== '' && (
                  <p className="mb-4">
                    {isJapanese ? 'その他' : 'Others'}:{etc}
                  </p>
                )}
              </div>
            );
          })}
          <div className={'flex justify-center items-center mt-8'}>
            <TransitionButton
              className="mr-2 px-5 min-w-[120px] w-[9rem]"
              label={tAuditionForm('TU002_048')}
              onClick={handleBack}
            />
            <Button className="w-[9rem]" label={tAuditionForm('TU002_058')} onClick={handleNext} />
          </div>
        </PageContent>
        <AppFooter />
      </div>
    </>
  );
};
